import React, { useCallback, useEffect, useState } from 'react';
import AccountCircle from '@mui/icons-material//AccountCircle';
import {
  Autocomplete,
  Box,
  Chip,
  createFilterOptions,
  FilterOptionsState,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { CustomPrimaryUser } from 'api';
import useGetCompanyUsers from 'services/hooks/useGetCompanyUsers';
import useGetFirmUsers from 'services/hooks/useGetFirmUsers';
import { Logger } from 'utilities';
import { CreateNewUserDialog } from '../CreateNewUserDialog';
import { ResponsibleUser, SourceResponsibleSelectorProps } from './types';
import { ResponsibleNewUserData, ResponsibleNewUserName } from '../CreateNewUserDialog/CreateNewUserDialog.props';

const StyledTypographyLabel = styled(Typography)(() => ({
  fontWeight: 'bold',
  marginBottom: 20,
}));

const StyledAccountCircle = styled(AccountCircle)(() => ({
  marginRight: 10,
}));

const SourceResponsibleSelector = ({
  responsibleUser,
  setResponsibleUser,
  responsibleUsers,
  currentFirmId,
  currentCompanyId,
  currentCompanyInfo,
  is409a,
  setIsSaving,
  isDisabled,
}: SourceResponsibleSelectorProps) => {
  const [newUser, setNewUser] = useState<ResponsibleNewUserData>();
  const [openUserDetailsDialog, setOpenUserDetailsDialog] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<ResponsibleUser[]>(responsibleUsers);
  const { enqueueSnackbar } = useSnackbar();

  const { getFirmUsers } = useGetFirmUsers(currentFirmId, '');
  const { getCompanyUsers } = useGetCompanyUsers(currentCompanyId, '');

  const selectedResponsibleUser = availableUsers.find(user => user.value === responsibleUser);

  const parseName = useCallback((text: string): ResponsibleNewUserName => {
    const fullName = text.slice(4).trim(); // Remove "Add " and trim any extra spaces
    const [firstName, ...lastNameParts] = fullName.split(' ');

    return {
      fullName,
      firstName,
      lastName: lastNameParts.join(' '), // Combine the remaining parts as the last name
    };
  }, []);

  const handleOnChange = (selectedResponsibleUser: ResponsibleUser | null): void => {
    if (selectedResponsibleUser && selectedResponsibleUser.value !== 0) {
      setResponsibleUser(selectedResponsibleUser.value);
    } else {
      const name = parseName(selectedResponsibleUser?.label ?? '');
      setNewUser(name);
      setOpenUserDetailsDialog(true);
    }
  };

  const reloadResponsibleUsers = useCallback(
    async newUserId => {
      try {
        enqueueSnackbar('Loading users...', {
          variant: 'info',
        });
        setIsSaving(true);
        const users = is409a ? await getCompanyUsers() : await getFirmUsers();
        const formattedCompanyUsers = users?.map((user: CustomPrimaryUser) => ({
          value: user.id!,
          label: `${user.first_name} ${user.last_name}`,
          isPrimaryContact: !!user.is_primary_contact,
        }));
        setAvailableUsers(formattedCompanyUsers ?? []);
        setResponsibleUser(newUserId);
      } catch (error) {
        Logger.error('Error loading users:', error);
      } finally {
        setIsSaving(false);
        enqueueSnackbar('Users loaded successfully', {
          variant: 'success',
        });
      }
    },
    [enqueueSnackbar, setIsSaving, is409a, getCompanyUsers, getFirmUsers, setResponsibleUser]
  );

  const handleOnCloseDialog = (newUserId?: number) => {
    setOpenUserDetailsDialog(false);
    if (newUserId) {
      reloadResponsibleUsers(newUserId);
    }
  };

  // Update availableUsers when responsibleUsers changes
  useEffect(() => {
    if (responsibleUsers?.length > 0) {
      setAvailableUsers(responsibleUsers);
    }
  }, [responsibleUsers]);

  const filter = createFilterOptions<ResponsibleUser>();

  const getFilteredOptions = useCallback(
    (options: ResponsibleUser[], params: FilterOptionsState<ResponsibleUser>) => {
      const filtered = filter(options, params);

      const { inputValue } = params;
      // Suggest the creation of a new value
      const isExisting = options.some(option => inputValue === option.label);

      if (inputValue !== '' && !isExisting) {
        const cleanInputValue = inputValue.replace(/^Add\s+/, '');
        filtered.push({
          value: 0,
          label: `Add ${cleanInputValue}`,
          isPrimaryContact: false,
        });
      }

      return filtered;
    },
    [filter]
  );

  return (
    <div>
      <StyledTypographyLabel variant="body1">Select source responsible for sending information</StyledTypographyLabel>

      <Autocomplete
        id="Select source responsible for sending information"
        options={availableUsers}
        style={{ width: '100%' }}
        autoHighlight
        getOptionLabel={option => option.label}
        onChange={(_event, option) => handleOnChange(option)}
        disableClearable
        clearOnEscape
        disabled={isDisabled}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        value={selectedResponsibleUser}
        filterOptions={getFilteredOptions}
        renderInput={params => (
          <TextField {...params} label="Select a responsible or add a new one" variant="standard" />
        )}
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          return (
            <Box
              key={option.value}
              {...rest}
              display="flex"
              alignItems="center"
              width="100%"
              justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1}>
                <StyledAccountCircle />
                <Typography variant="body1" noWrap>
                  {option.label}
                </Typography>
              </Box>
              {option.isPrimaryContact && <Chip label="Primary Contact" color="primary" size="small" />}
            </Box>
          );
        }}
      />
      {newUser && (
        <CreateNewUserDialog
          open={openUserDetailsDialog}
          onClose={handleOnCloseDialog}
          newUser={newUser}
          currentFirmId={currentFirmId}
          currentCompanyInfo={currentCompanyInfo}
          is409a={is409a}
        />
      )}
    </div>
  );
};

export default SourceResponsibleSelector;
